// Self-hosted auth client — wraps Zero.Auth JWT session handling.
// Zero.Auth (via the Gateway) issues an HS256 access token (~1h) plus a
// rotating opaque refresh token (~30d). "Keep me signed in" → localStorage
// (survives restarts); otherwise sessionStorage (dies with the tab).

import type { QueryClient } from '@tanstack/react-query';
import { setTenantId } from './tenantStore';
import { setSuperAdminClubId } from './superAdminStore';

const ACCESS_KEY = 'argus-access-token';
const REFRESH_KEY = 'argus-refresh-token';
const EXPIRES_KEY = 'argus-token-expires-at';

const cfg = (window as any).__CONFIG__ ?? {};
const API_BASE: string = cfg.API_BASE_URL || import.meta.env.VITE_API_BASE_URL || '/api';

interface TokenResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number; // seconds
  mustChangePassword: boolean;
}

export interface LoginOutcome {
  mustChangePassword: boolean;
}

function activeStorage(): Storage {
  return localStorage.getItem(REFRESH_KEY) !== null ? localStorage : sessionStorage;
}

function saveTokens(tokens: TokenResponse, store: Storage): void {
  store.setItem(ACCESS_KEY, tokens.accessToken);
  store.setItem(REFRESH_KEY, tokens.refreshToken);
  store.setItem(EXPIRES_KEY, String(Date.now() + tokens.expiresIn * 1000));
}

export function clearSession(): void {
  for (const store of [localStorage, sessionStorage]) {
    store.removeItem(ACCESS_KEY);
    store.removeItem(REFRESH_KEY);
    store.removeItem(EXPIRES_KEY);
  }
  // The tenant and acted-as club are part of the session, not decoration. They were left
  // behind by every teardown, and clearing them through the STORES (not by deleting the
  // sessionStorage keys directly) is what matters: both stores cache the value in a
  // module-level variable that `api.ts` reads at request time, so a raw
  // `sessionStorage.removeItem('tenant-id')` — which two sign-out buttons did — leaves
  // `getTenantId()` still returning the previous club until the page reloads.
  setTenantId(null);
  setSuperAdminClubId(null);
}

/**
 * End the session completely: revoke the refresh token, drop every token, reset the tenant
 * and acted-as club, and empty the query cache.
 *
 * There were four sign-out buttons and each re-implemented this differently — Layout and the
 * Player Page cleared the cache and removed the tenant KEY (leaving the in-memory copy),
 * ChooseClubPage cleared only the cache, and Onboarding cleared nothing at all. On a shared
 * machine that left the next person's first requests carrying the previous club's tenant, and
 * the previous club's answers already sitting in the cache. Worse for a super admin: `appGate`
 * treats any surviving club context as a valid selection, so the next super admin to sign in
 * was dropped straight into the club the last one had been acting as, without choosing it.
 *
 * Call this instead of `logout()` from anything that signs a user out.
 */
export async function endSession(queryClient?: QueryClient): Promise<void> {
  await logout();
  queryClient?.clear();
}

/** Synchronous session probe used by route guards. */
export function hasSession(): boolean {
  return (
    localStorage.getItem(REFRESH_KEY) !== null || sessionStorage.getItem(REFRESH_KEY) !== null
  );
}

async function postJson(path: string, body: unknown, token?: string): Promise<Response> {
  return fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
}

export async function login(
  email: string,
  password: string,
  keepSignedIn: boolean,
): Promise<LoginOutcome> {
  const res = await postJson('/auth/login', { email, password });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}) as { error?: string });
    throw new Error(data.error ?? 'Sign-in failed. Please try again.');
  }
  const tokens = (await res.json()) as TokenResponse;
  clearSession();
  saveTokens(tokens, keepSignedIn ? localStorage : sessionStorage);
  return { mustChangePassword: tokens.mustChangePassword };
}

// Single-flight refresh: concurrent callers share one in-flight request so a
// burst of 401s can't burn the (one-time-use) refresh token more than once.
let refreshInFlight: Promise<string | null> | null = null;

export function forceRefresh(): Promise<string | null> {
  refreshInFlight ??= (async () => {
    try {
      const store = activeStorage();
      const refreshToken = store.getItem(REFRESH_KEY);
      if (!refreshToken) return null;
      const res = await postJson('/auth/refresh', { refreshToken });
      if (!res.ok) {
        clearSession();
        return null;
      }
      const tokens = (await res.json()) as TokenResponse;
      saveTokens(tokens, store);
      return tokens.accessToken;
    } catch {
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

/** Valid access token, refreshing when expired or within 60s of expiry. */
export async function getAuthToken(): Promise<string | null> {
  const store = activeStorage();
  const access = store.getItem(ACCESS_KEY);
  const expiresAt = Number(store.getItem(EXPIRES_KEY) ?? 0);
  if (access && Date.now() < expiresAt - 60_000) return access;
  if (!hasSession()) return null;
  return forceRefresh();
}

export async function logout(): Promise<void> {
  const refreshToken = activeStorage().getItem(REFRESH_KEY);
  if (refreshToken) {
    try {
      await postJson('/auth/logout', { refreshToken });
    } catch {
      // Best-effort — the server-side token also dies at its natural expiry.
    }
  }
  clearSession();
}

export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  const token = await getAuthToken();
  const res = await postJson('/auth/change-password', { currentPassword, newPassword }, token ?? undefined);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}) as { error?: string });
    throw new Error(data.error ?? 'Password change failed.');
  }
  // Change-password revokes all old refresh tokens and returns a fresh pair.
  const tokens = (await res.json()) as TokenResponse;
  saveTokens(tokens, activeStorage());
}
